/**
 * Session-scoped cache for read-only public RPCs.
 *
 * React Query's staleTime only survives inside a single page load — every
 * fresh navigation/reload re-hits the database. These public preview/stats
 * RPCs are the hottest queries in the project and their data changes slowly,
 * so we back them with sessionStorage and only go to the database when the
 * cached copy is older than the TTL.
 */
import { supabase } from "@/integrations/supabase/client";

const PREFIX = "tgh:rpc:";

type Entry<T> = { t: number; v: T };

const readCache = <T,>(key: string, ttlMs: number): T | null => {
  try {
    const raw = sessionStorage.getItem(PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Entry<T>;
    if (!parsed || typeof parsed.t !== "number") return null;
    if (Date.now() - parsed.t > ttlMs) return null;
    return parsed.v;
  } catch {
    return null;
  }
};

const writeCache = <T,>(key: string, value: T): void => {
  try {
    sessionStorage.setItem(PREFIX + key, JSON.stringify({ t: Date.now(), v: value } satisfies Entry<T>));
  } catch {
    // storage full / disabled — caching is best-effort
  }
};

/** In-flight de-duplication so parallel components share one request. */
const inflight = new Map<string, Promise<unknown>>();

/**
 * Call a Supabase RPC through the session cache.
 * @param fn RPC name
 * @param args RPC arguments (also part of the cache key)
 * @param ttlMs how long a cached result stays good (default 5 min)
 */
export const cachedRpc = async <T,>(
  fn: string,
  args?: Record<string, unknown>,
  ttlMs = 5 * 60_000,
): Promise<T | null> => {
  const key = args ? `${fn}:${JSON.stringify(args)}` : fn;

  const hit = readCache<T>(key, ttlMs);
  if (hit !== null) return hit;

  const existing = inflight.get(key);
  if (existing) return (await existing) as T | null;

  const p = (async () => {
    const { data, error } = await (supabase.rpc as any)(fn, args);
    if (error) throw error;
    if (data !== null && data !== undefined) writeCache(key, data);
    return data as T;
  })().finally(() => inflight.delete(key));

  inflight.set(key, p);
  return (await p) as T | null;
};

export default cachedRpc;

/** Seed the session cache with a value fetched some other way (e.g. a bundled RPC). */
export const seedRpcCache = <T,>(fn: string, args: Record<string, unknown> | undefined, value: T): void => {
  const key = args ? `${fn}:${JSON.stringify(args)}` : fn;
  if (value !== null && value !== undefined) writeCache(key, value);
};

/** True when a fresh cached value already exists for this RPC. */
export const hasFreshRpcCache = (fn: string, args?: Record<string, unknown>, ttlMs = 5 * 60_000): boolean => {
  const key = args ? `${fn}:${JSON.stringify(args)}` : fn;
  return readCache(key, ttlMs) !== null;
};

/**
 * Drop cached public stats/previews so the next read hits the database.
 * Called after events that meaningfully change the numbers (e.g. a fresh
 * Discord join sync), instead of polling on a timer.
 */
export const clearRpcCache = (): void => {
  try {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k?.startsWith(PREFIX)) keys.push(k);
    }
    keys.forEach((k) => sessionStorage.removeItem(k));
  } catch {
    // ignore
  }
  inflight.clear();
};
